import { useRef } from 'react'
import { ImageDown } from 'lucide-react'
import { BarChart } from '@/components/charts/BarChart'
import { LineChart } from '@/components/charts/LineChart'
import { DoughnutChart } from '@/components/charts/DoughnutChart'
import { PieChart } from '@/components/charts/PieChart'
import { ScatterChart } from '@/components/charts/ScatterChart'
import { BubbleChart } from '@/components/charts/BubbleChart'
import { RadarChart } from '@/components/charts/RadarChart'
import { PolarAreaChart } from '@/components/charts/PolarAreaChart'
import { Button } from '@/components/ui/button'
import type { ChatMessagePart } from '../types'
import { downloadChartAsImage } from '../export'
import { ShareButton } from './actionBar'

type ChartPart = Extract<ChatMessagePart, { kind: 'chart' }>

function asNumbers(data: unknown): number[] {
  if (!Array.isArray(data)) return []
  return data
    .map((v) => {
      if (typeof v === 'number') return v
      if (v && typeof v === 'object' && 'y' in (v as Record<string, unknown>)) {
        const y = (v as { y?: unknown }).y
        return typeof y === 'number' ? y : Number(y ?? 0)
      }
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    })
    .filter((n) => Number.isFinite(n))
}

function asScatterPoints(data: unknown, labels: string[]): { x: number; y: number }[] {
  if (!Array.isArray(data)) return []
  return data
    .map((v, i): { x: number; y: number } | null => {
      if (v && typeof v === 'object' && 'x' in (v as Record<string, unknown>) && 'y' in (v as Record<string, unknown>)) {
        const p = v as { x: unknown; y: unknown }
        const x = Number(p.x)
        const y = Number(p.y)
        if (Number.isFinite(x) && Number.isFinite(y)) return { x, y }
        return null
      }
      // Fall back: treat [numbers] + labels as (index/labelNum, value) pairs.
      const y = Number(v)
      if (!Number.isFinite(y)) return null
      const xFromLabel = Number(labels[i])
      return { x: Number.isFinite(xFromLabel) ? xFromLabel : i, y }
    })
    .filter((p): p is { x: number; y: number } => p !== null)
}

function asBubblePoints(data: unknown): { x: number; y: number; r: number }[] {
  if (!Array.isArray(data)) return []
  return data
    .map((v): { x: number; y: number; r: number } | null => {
      if (v && typeof v === 'object' && 'x' in (v as Record<string, unknown>) && 'y' in (v as Record<string, unknown>)) {
        const p = v as { x: unknown; y: unknown; r?: unknown }
        const x = Number(p.x)
        const y = Number(p.y)
        const r = p.r != null ? Number(p.r) : 8
        if (Number.isFinite(x) && Number.isFinite(y)) return { x, y, r: Number.isFinite(r) ? r : 8 }
      }
      return null
    })
    .filter((p): p is { x: number; y: number; r: number } => p !== null)
}

interface Props {
  part: ChartPart
  onShare?: () => void
}

export function ChartBlock({ part, onShare }: Props) {
  const { chartType, title, data } = part
  const labels = data.labels ?? []
  const datasets = data.datasets ?? []
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const exportTitle = title || `${chartType} chart`

  let chart: React.ReactNode = null
  if (chartType === 'line') {
    // Pass `color` through so a follow-up like "make it red" actually
    // recolours the line. Without this the LLM emits color: 'red' on
    // the dataset, the BFF + tool serialise it onto the part, and
    // ChartBlock used to silently drop it - leaving the chart green
    // and the model in a "I updated it!" lie.
    chart = (
      <LineChart
        labels={labels}
        series={datasets.map((ds) => ({
          label: ds.label ?? '',
          values: asNumbers(ds.data),
          color: ds.color,
        }))}
        height={280}
      />
    )
  } else if (chartType === 'bar') {
    chart = (
      <BarChart
        labels={labels}
        series={datasets.map((ds) => ({
          label: ds.label ?? '',
          values: asNumbers(ds.data),
          color: ds.color,
        }))}
        height={280}
      />
    )
  } else if (chartType === 'doughnut') {
    chart = <DoughnutChart labels={labels} values={asNumbers(datasets[0]?.data)} height={240} />
  } else if (chartType === 'pie') {
    chart = <PieChart labels={labels} values={asNumbers(datasets[0]?.data)} height={280} />
  } else if (chartType === 'scatter') {
    chart = (
      <ScatterChart
        series={datasets.map((ds) => ({
          label: ds.label ?? '',
          points: asScatterPoints(ds.data, labels),
          color: ds.color,
        }))}
        xAxisLabel={data.xAxisLabel}
        yAxisLabel={data.yAxisLabel}
        height={320}
      />
    )
  } else if (chartType === 'bubble') {
    chart = (
      <BubbleChart
        series={datasets.map((ds) => ({
          label: ds.label ?? '',
          points: asBubblePoints(ds.data),
          color: ds.color,
        }))}
        xAxisLabel={data.xAxisLabel}
        yAxisLabel={data.yAxisLabel}
        height={320}
      />
    )
  } else if (chartType === 'radar') {
    chart = (
      <RadarChart
        labels={labels}
        series={datasets.map((ds) => ({
          label: ds.label ?? '',
          values: asNumbers(ds.data),
          color: ds.color,
        }))}
        height={320}
      />
    )
  } else if (chartType === 'polarArea') {
    chart = <PolarAreaChart labels={labels} values={asNumbers(datasets[0]?.data)} height={320} />
  }

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        {title ? (
          <div className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </div>
        ) : <span />}
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => downloadChartAsImage(wrapperRef.current, exportTitle, 'image/jpeg')}
            className="size-7"
            title="Download as image"
            aria-label="Download as image"
          >
            <ImageDown className="size-3.5" />
          </Button>
          <ShareButton onClick={onShare} />
        </div>
      </div>
      <div ref={wrapperRef}>{chart}</div>
    </div>
  )
}
